<template>
  <div class="list-owner-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listOwner.title')" :subtitle="$t('listOwner.page.subtitle')">
      <template #extra>
        <el-button type="success" icon="el-icon-s-check" :loading="authLoading" v-if="owners.length > 0"
          @click="batchAutoAuth">
          {{ $t('listOwner.buttons.batchAutoAuth') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openAddOwnerModal">
          {{ $t('listOwner.buttons.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <el-row :gutter="16">
      <!-- 左侧人员角色筛选 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="4">
        <div class="ds-panel ds-role-tree">
          <div class="ds-role-tree__title">{{ $t('listOwner.page.roleFilter') }}</div>
          <ul class="ds-role-tree__list">
            <li v-for="(item, index) in personRoles" :key="index" class="ds-role-tree__item"
              :class="{ 'is-active': conditions.personRole === item.statusCd }" @click="switchPersonRole(item)">
              {{ item.name }}
            </li>
          </ul>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="20">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.personType" :placeholder="$t('listOwner.personTypeName')" clearable
                class="w-full">
                <el-option v-for="(type, index) in personTypes" :key="index" :label="type.label" :value="type.value" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.name" :placeholder="$t('listOwner.placeholders.name')"
                @keyup.enter.native="queryOwnerMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.roomName" :placeholder="$t('listOwner.placeholders.room')"
                @keyup.enter.native="queryOwnerMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.link" :placeholder="$t('listOwner.placeholders.phone')"
                @keyup.enter.native="queryOwnerMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.idCard" :placeholder="$t('listOwner.placeholders.idCard')"
                @keyup.enter.native="queryOwnerMethod" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryOwnerMethod">
              {{ $t('listOwner.buttons.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetOwnerMethod">
              {{ $t('listOwner.buttons.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="listOwnerData">
            <template #left>
              <span class="ds-panel__title">{{ $t('listOwner.ownerInfo') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="owners" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('listOwner.page.empty')" @retry="listOwnerData">
            <el-table-column :label="$t('listOwner.columns.face')" width="80" align="center">
              <template slot-scope="scope">
                <el-image style="width:60px;height:60px;border-radius:5px;cursor:pointer;"
                  :src="scope.row.faceUrl || '/img/noPhoto.jpg'" :preview-src-list="[scope.row.faceUrl]" fit="cover" />
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.name')" align="center" min-width="110">
              <template slot-scope="scope">
                <div>{{ scope.row.name }}</div>
                <div v-if="scope.row.personType === 'C'" class="ds-cell-sub">{{ scope.row.concactPerson }}</div>
              </template>
            </el-table-column>

            <el-table-column prop="personTypeName" :label="$t('listOwner.columns.type')" align="center" width="100" />

            <el-table-column prop="personRoleName" :label="$t('listOwner.columns.role')" align="center" width="110" />

            <el-table-column :label="$t('listOwner.columns.gender')" align="center" width="80">
              <template slot-scope="scope">
                {{ scope.row.sex == 0 ? $t('listOwner.gender.male') : $t('listOwner.gender.female') }}
              </template>
            </el-table-column>

            <el-table-column prop="idCard" :label="$t('listOwner.columns.idCard')" align="center" min-width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.idCard || '-' }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="link" :label="$t('listOwner.columns.phone')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.link || '-' }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="concactLink" :label="$t('listOwner.columns.backupPhone')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.concactLink || '-' }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="address" :label="$t('listOwner.columns.address')" align="center" min-width="150"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.address || '-' }}
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.rooms')" align="center" width="90">
              <template slot-scope="scope">
                <el-link type="primary" @click="viewOwnerRooms(scope.row)">
                  {{ scope.row.roomCount || 0 }}
                </el-link>
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.members')" align="center" width="100">
              <template slot-scope="scope">
                <el-link v-if="scope.row.ownerTypeCd === '1001'" type="primary" @click="viewOwnerMembers(scope.row)">
                  {{ scope.row.memberCount || 0 }}
                </el-link>
                <span v-else>-</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.cars')" align="center" width="90">
              <template slot-scope="scope">
                <el-link type="primary" @click="viewOwnerCars(scope.row)">
                  {{ scope.row.carCount || 0 }}
                </el-link>
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.debts')" align="right" width="110">
              <template slot-scope="scope">
                <el-link type="primary" @click="viewOweFees(scope.row)">
                  {{ formatMoney(scope.row.oweFee) }}
                </el-link>
              </template>
            </el-table-column>

            <el-table-column v-for="(item, index) in listColumns" :key="index" :label="item" align="center"
              min-width="110">
              <template slot-scope="scope">
                {{ scope.row.listValues[index] || '-' }}
              </template>
            </el-table-column>

            <el-table-column :label="$t('listOwner.columns.operation')" width="360" align="center" fixed="right">
              <template slot-scope="scope">
                <el-dropdown>
                  <el-button type="primary" size="mini">
                    {{ $t('listOwner.buttons.edit') }}
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item @click.native="openEditOwnerModal(scope.row)">
                      {{ $t('listOwner.actions.editOwner') }}
                    </el-dropdown-item>
                    <el-dropdown-item @click.native="openDeleteOwnerModal(scope.row)">
                      {{ $t('listOwner.actions.deleteOwner') }}
                    </el-dropdown-item>
                    <el-dropdown-item @click.native="openAddOwnerRoom(scope.row)">
                      {{ $t('listOwner.actions.addRoom') }}
                    </el-dropdown-item>
                    <el-dropdown-item @click.native="openDeleteOwnerRoom(scope.row)">
                      {{ $t('listOwner.buttons.unbindRoom') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>

                <el-button v-if="scope.row.ownerTypeCd === '1001'" type="info" size="mini"
                  @click="openOwnerDetailMember(scope.row)">
                  {{ $t('listOwner.buttons.member') }}
                </el-button>

                <el-button type="info" size="mini" @click="openOwnerDetail(scope.row)">
                  {{ $t('listOwner.buttons.detail') }}
                </el-button>

                <el-button type="success" size="mini" @click="autoAuthOwner(scope.row)"
                  :loading="scope.row.authLoading">
                  {{ $t('listOwner.buttons.autoAuth') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="total" :page="currentPage" :size="pageSize" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handlePageChange" />
        </div>
      </el-col>
    </el-row>

    <add-owner ref="addOwner" @success="handleOwnerSuccess" />
    <edit-owner ref="editOwner" @success="handleOwnerSuccess" />
    <delete-owner ref="deleteOwner" @success="handleOwnerSuccess" />
    <owner-rooms ref="ownerRooms" />
    <owner-members ref="ownerMembers" />
    <owner-cars ref="ownerCars" />
    <owner-owe-fees ref="ownerOweFees" />
  </div>
</template>

<script>
import { queryOwners, batchAutoAuth, autoAuthOwner } from '@/api/owner/ownerApi'
import AddOwner from '@/components/owner/addOwner'
import EditOwner from '@/components/owner/editOwner'
import DeleteOwner from '@/components/owner/deleteOwner'
import { getAttrSpecList } from '@/api/dev/attrSpecApi'
import OwnerRooms from '@/components/owner/ownerRooms'
import OwnerMembers from '@/components/owner/ownerMembers'
import OwnerCars from '@/components/owner/ownerCars'
import OwnerOweFees from '@/components/owner/ownerOweFees'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListOwner',
  components: {
    AddOwner,
    EditOwner,
    DeleteOwner,
    OwnerRooms,
    OwnerMembers,
    OwnerCars,
    OwnerOweFees,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      authLoading: false,
      density: 'default',
      owners: [],
      personRoles: [],
      personTypes: [
        { value: '', label: this.$t('common.select') },
        { value: 'P', label: this.$t('listOwner.personType.personal') },
        { value: 'C', label: this.$t('listOwner.personType.company') }
      ],
      conditions: {
        personRole: '',
        name: '',
        roomName: '',
        link: '',
        idCard: '',
        personType: ''
      },
      listColumns: [],
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  created() {
    this.getPersonRoles()
    this.getColumns()
    this.listOwnerData()
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value)
    },
    async getPersonRoles() {
      this.personRoles = [
        { statusCd: '', name: this.$t('listOwner.role.all') },
        { statusCd: '1', name: this.$t('listOwner.role.owner') },
        { statusCd: '2', name: this.$t('listOwner.role.tenant') }
      ]
    },

    async getColumns() {
      try {
        const { data } = await getAttrSpecList({
          page: 1,
          row: 100,
          tableName: 'building_owner_attr'
        })
        this.listColumns = data
          .filter(item => item.specShow === 'Y')
          .map(item => item.specName)
      } catch (error) {
        console.error('Failed to get columns:', error)
      }
    },

    async listOwnerData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page: this.currentPage,
          row: this.pageSize
        }
        const { data, total } = await queryOwners(params)
        this.owners = data
        this.total = total
        this.dealOwnerAttr(data)
      } catch (error) {
        console.error('Failed to get owner list:', error)
        this.hasError = true
        this.errorMessage = this.$t('listOwner.fetchError')
      } finally {
        this.loading = false
      }
    },

    dealOwnerAttr(owners) {
      owners.forEach(owner => {
        owner.listValues = []
        if (!owner.ownerAttrDtos || owner.ownerAttrDtos.length === 0) return

        this.listColumns.forEach(column => {
          const attr = owner.ownerAttrDtos.find(dto => dto.specName === column)
          owner.listValues.push(attr ? attr.valueName : '')
        })
      })
    },

    switchPersonRole(item) {
      this.conditions.personRole = item.statusCd
      this.listOwnerData()
    },

    queryOwnerMethod() {
      this.currentPage = 1
      this.listOwnerData()
    },

    resetOwnerMethod() {
      this.conditions = {
        personRole: '',
        name: '',
        roomName: '',
        link: '',
        idCard: '',
        personType: ''
      }
      this.listOwnerData()
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.listOwnerData()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.listOwnerData()
    },

    openAddOwnerModal() {
      this.$refs.addOwner.open()
    },

    openEditOwnerModal(owner) {
      this.$refs.editOwner.open(owner)
    },

    openDeleteOwnerModal(owner) {
      this.$refs.deleteOwner.open(owner)
    },

    handleOwnerSuccess() {
      this.listOwnerData()
    },

    viewOwnerRooms(owner) {
      console.log('View owner rooms:', owner)
      // 实际项目中跳转到房屋页面
      this.$refs.ownerRooms.open(owner)

    },

    viewOwnerMembers(owner) {
      console.log('View owner members:', owner)
      // 实际项目中跳转到成员页面
      this.$refs.ownerMembers.open(owner.ownerId)

    },

    viewOwnerCars(owner) {
      console.log('View owner cars:', owner)
      // 实际项目中跳转到车辆页面
      this.$refs.ownerCars.open(owner.ownerId)

    },

    viewOweFees(owner) {
      console.log('View owe fees:', owner)
      // 实际项目中跳转到欠费页面
      this.$refs.ownerOweFees.open(owner.ownerId)

    },

    openAddOwnerRoom(owner) {
      console.log('Add owner room:', owner)
      // 实际项目中跳转到绑定房屋页面
      this.$router.push(`/views/owner/ownerBindRoom?ownerId=${owner.ownerId}`)

    },

    openDeleteOwnerRoom(owner) {
      console.log('Delete owner room:', owner)
      // 实际项目中跳转到解绑房屋页面
      this.$router.push(`/views/owner/deleteOwnerRoom?ownerId=${owner.ownerId}`)

    },

    openOwnerDetail(owner) {
      console.log('Owner detail:', owner)
      // 实际项目中跳转到详情页面
      this.$router.push(`/views/owner/ownerDetail?ownerId=${owner.ownerId}&needBack=Y`)

    },

    openOwnerDetailMember(owner) {
      console.log('Owner detail member:', owner)
      // 实际项目中跳转到成员详情页面
      this.$router.push(`/views/owner/ownerDetail?ownerId=${owner.ownerId}`)

    },

    async batchAutoAuth() {
      if (this.owners.length === 0) {
        this.$message.warning(this.$t('listOwner.messages.noOwners'))
        return
      }

      try {
        await this.$confirm(
          this.$t('listOwner.messages.confirmBatchAuth', { count: this.owners.length }),
          this.$t('common.tips'),
          { type: 'warning' }
        )
      } catch {
        return
      }

      this.authLoading = true
      try {
        const ownerIds = this.owners.map(o => o.ownerId)
        const res = await batchAutoAuth(ownerIds)

        // 详细展示结果
        let message = this.$t('listOwner.messages.batchAuthResult') + '\n\n'
        message += this.$t('listOwner.messages.authSuccess') + ': ' + res.successCount + '\n'
        message += this.$t('listOwner.messages.authFail') + ': ' + res.failCount + '\n'

        if (res.skipNoUserIdCount > 0) {
          message += '\n' + this.$t('listOwner.messages.skipNoUserId') + ': ' + res.skipNoUserIdCount + '\n'
          const names = res.skipNoUserIdList.map(item => item.name).join('、')
          message += '   ' + names + '\n'
        }

        if (res.skipNoRoomCount > 0) {
          message += '\n' + this.$t('listOwner.messages.skipNoRoom') + ': ' + res.skipNoRoomCount + '\n'
          const names = res.skipNoRoomList.map(item => item.name).join('、')
          message += '   ' + names + '\n'
        }

        this.$alert(message, this.$t('listOwner.messages.authResult'), {
          confirmButtonText: this.$t('common.confirm'),
          dangerouslyUseHTMLString: true
        })

        this.listOwnerData() // 刷新列表
      } catch (error) {
        this.$message.error(error.message || this.$t('common.operationFailed'))
      } finally {
        this.authLoading = false
      }
    },

    async autoAuthOwner(owner) {
      try {
        await this.$confirm(
          this.$t('listOwner.messages.confirmSingleAuth', { name: owner.name }),
          this.$t('common.tips'),
          { type: 'warning' }
        )
      } catch {
        return
      }

      this.$set(owner, 'authLoading', true)
      try {
        const res = await autoAuthOwner(owner.ownerId)
        let msg = this.$t('listOwner.messages.singleAuthSuccess')
        if (res.createdAccountCount > 0) {
          msg += '（已创建账号）'
        }
        this.$message.success(msg)
        this.listOwnerData()
      } catch (error) {
        this.$message.error(error.message || this.$t('common.operationFailed'))
      } finally {
        this.$set(owner, 'authLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.list-owner-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

/* 单元格次要说明 */
.ds-cell-sub {
  font-size: var(--ds-font-size-sm);
  color: var(--ds-gray-500);
}

/* 左侧人员角色筛选 */
.ds-role-tree {
  padding: var(--ds-spacing-md) 0;

  &__title {
    padding: 0 var(--ds-spacing-lg) var(--ds-spacing-sm);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-600);
  }

  &__list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__item {
    padding: 10px var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    text-align: center;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-gray-100);
    }

    &.is-active {
      background-color: var(--ds-primary-sub);
      border-left-color: var(--ds-primary);
      color: var(--ds-primary);
      font-weight: 600;
    }
  }
}
</style>
